import type { NextRequest } from 'next/server';
import { ONETRUST_CATEGORIES, type OneTrustCategory } from './index';
import { parseOptanonConsentGroups } from './parseOptanonConsent';

/**
 * Server-side OneTrust consent helpers.
 *
 * The client-side dispatcher in `./index.ts` reads `window.OnetrustActiveGroups`;
 * here we mirror the same semantics against the request: parse the
 * `OptanonConsent` cookie, fall back to the `Sec-GPC: 1` request header when
 * the cookie is absent (so GPC opt-outs are respected before OneTrust hydrates),
 * and default to opt-in otherwise.
 */

export const OPTANON_CONSENT_COOKIE = 'OptanonConsent';

function gpcEnabled(secGpcHeader: string | null | undefined): boolean {
  return secGpcHeader?.trim() === '1';
}

/**
 * Returns true when the user has consented to `category`.
 *
 * Rules:
 *   - OptanonConsent cookie present + category granted -> true
 *   - OptanonConsent cookie present + category denied  -> false
 *   - OptanonConsent cookie absent + Sec-GPC: 1        -> false (GPC opt-out
 *     before OneTrust hydrates)
 *   - OptanonConsent cookie absent + no GPC             -> true (default opt-in)
 *
 * Strictly Necessary (C0001) is always granted.
 */
export function hasConsentFromHeaders(
  optanonConsentCookie: string | undefined,
  secGpcHeader: string | null | undefined,
  category: OneTrustCategory,
): boolean {
  if (category === ONETRUST_CATEGORIES.STRICTLY_NECESSARY) return true;

  if (optanonConsentCookie) {
    return parseOptanonConsentGroups(optanonConsentCookie).includes(category);
  }

  return !gpcEnabled(secGpcHeader);
}

/**
 * Convenience for route handlers / server libs that already hold a NextRequest.
 */
export function hasConsentFromRequest(request: NextRequest, category: OneTrustCategory): boolean {
  return hasConsentFromHeaders(
    request.cookies.get(OPTANON_CONSENT_COOKIE)?.value,
    request.headers.get('sec-gpc'),
    category,
  );
}
